function createElement(tagName, className = '', text = null) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== null) element.textContent = text;
  return element;
}

function createEmptyState(message, icon = '') {
  const state = createElement('div', 'empty-state cost-empty-state');
  if (icon) state.append(createElement('div', 'empty-icon', icon));
  state.append(createElement('p', '', message));
  return state;
}

export function renderCostStatusState({ content, overview, message, tone }) {
  if (overview) {
    const card = createElement('div', 'cost-summary-card');
    card.append(
      createElement('div', 'cost-summary-label', tone === 'error' ? '错误' : '费用预览'),
      createElement('div', `cost-summary-value${tone === 'error' ? ' cost-summary-value-error' : ''}`, message),
    );
    overview.replaceChildren(card);
  }
  if (content) content.replaceChildren(createEmptyState(message, tone === 'error' ? '⚠️' : '⏳'));
}

export function renderTopUsersView(root, { topUsers, totals, formatCurrency }) {
  if (!root) return;
  const card = createElement('div', 'cost-top-users-card');
  card.append(createElement('div', 'cost-config-title', 'Top 5 消耗用户'));

  if (!topUsers.length) {
    card.append(createEmptyState('暂无用户消耗数据'));
    root.replaceChildren(card);
    return;
  }

  const scroll = createElement('div', 'cost-table-scroll');
  const table = createElement('table', 'cost-table cost-top-table');
  const headerRow = createElement('tr');
  [
    ['用户', 'th-user'],
    ['图片算力 (¥)', 'th-cost'],
    ['市场工具 (¥)', 'th-cost'],
    ['模型消耗 (¥)', 'th-cost'],
    ['合计 (¥)', 'th-cost'],
  ].forEach(([label, className]) => headerRow.append(createElement('th', className, label)));
  const thead = document.createElement('thead');
  thead.append(headerRow);
  const tbody = document.createElement('tbody');

  topUsers.forEach((user) => {
    const row = document.createElement('tr');
    const userCell = createElement('td', 'td-user');
    const userInfo = createElement('div', 'cost-top-user-cell');
    const name = user.name || user.email || '未命名';
    userInfo.append(
      createElement('div', 'cost-top-avatar', (user.name || user.email || 'NA').slice(0, 2).toUpperCase()),
      createElement('span', '', name),
    );
    userCell.append(userInfo);
    row.append(
      userCell,
      createElement('td', 'td-num', formatCurrency(user.costs?.linkfoxCny || 0)),
      createElement('td', 'td-num', formatCurrency(user.costs?.marketDataCny || 0)),
      createElement('td', 'td-num', formatCurrency(user.costs?.textModelCny || 0)),
      createElement('td', 'td-total', formatCurrency(user.costs?.totalCny || 0)),
    );
    tbody.append(row);
  });

  const footerRow = createElement('tr', 'cost-top-footer-row');
  footerRow.append(
    createElement('td', 'td-user td-footer-label', '合计'),
    createElement('td', 'td-num td-footer-num', formatCurrency(totals.image)),
    createElement('td', 'td-num td-footer-num', formatCurrency(totals.market)),
    createElement('td', 'td-num td-footer-num', formatCurrency(totals.model)),
    createElement('td', 'td-total td-footer-total', formatCurrency(totals.total)),
  );
  const tfoot = document.createElement('tfoot');
  tfoot.append(footerRow);
  table.append(thead, tbody, tfoot);
  scroll.append(table);
  card.append(scroll);
  root.replaceChildren(card);
}

function createUsageLimitCell(limit = {}, resource, { formatCount, statusLabel }) {
  const enabled = Boolean(limit.enabled);
  const status = ['blocked', 'watching', 'normal'].includes(String(limit.status))
    ? String(limit.status)
    : (enabled ? 'normal' : 'unlimited');
  const limitText = enabled && Number(limit.limitUnits || 0) > 0
    ? `${formatCount(limit.limitUnits)} ${resource.unit}`
    : '无上限';
  const usedText = `${formatCount(limit.usedUnits || 0)} ${resource.unit}`;
  const remaining = enabled && limit.remainingUnits !== null && limit.remainingUnits !== undefined
    ? `${formatCount(limit.remainingUnits)} ${resource.unit}`
    : '不限制';
  const cell = createElement('td', 'cost-limit-resource-cell');
  const wrapper = createElement('div', 'cost-limit-cell');
  const usage = createElement('div', 'cost-limit-usage-line');
  usage.append(
    createElement('span', 'cost-mono', `已用 ${usedText}`),
    createElement('span', `cost-limit-badge status-${status}`, statusLabel(status)),
  );
  wrapper.append(
    usage,
    createElement('div', 'cost-muted', `上限 ${limitText}`),
    createElement('div', 'cost-muted', `剩余 ${remaining} · ${limit.periodType === 'monthly' ? '每月' : '每日'}`),
  );
  cell.append(wrapper);
  return cell;
}

export function renderUsageLimitTableView(root, {
  users,
  allSelected,
  resources,
  selectedUserIds,
  formatCurrency,
  formatCount,
  statusLabel,
}) {
  if (!root) return;
  if (!users.length) {
    root.replaceChildren(createEmptyState('暂无用户限额数据'));
    return;
  }

  const scroll = createElement('div', 'cost-table-scroll');
  const table = createElement('table', 'cost-table cost-limit-table');
  const colgroup = document.createElement('colgroup');
  ['cost-limit-col-check', 'cost-limit-col-user', 'cost-limit-col-resource', 'cost-limit-col-resource', 'cost-limit-col-resource', 'cost-limit-col-action']
    .forEach((className) => colgroup.append(createElement('col', className)));
  const thead = document.createElement('thead');
  const headerRow = document.createElement('tr');
  const selectAllCell = createElement('th', 'cost-limit-check-col');
  const selectAll = document.createElement('input');
  selectAll.type = 'checkbox';
  selectAll.id = 'cost-limit-select-all';
  selectAll.checked = allSelected;
  selectAllCell.append(selectAll);
  headerRow.append(selectAllCell, createElement('th', '', '用户'));
  resources.forEach((resource) => headerRow.append(createElement('th', '', resource.label)));
  headerRow.append(createElement('th', '', '操作'));
  thead.append(headerRow);

  const tbody = document.createElement('tbody');
  users.forEach((user) => {
    const userId = Number(user.userId);
    const row = document.createElement('tr');
    const checkCell = createElement('td', 'cost-limit-check-col');
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'cost-limit-user-check';
    checkbox.dataset.userId = String(userId);
    checkbox.checked = selectedUserIds.has(userId);
    checkCell.append(checkbox);

    const userCell = createElement('td', 'cost-limit-user-cell');
    const userMain = createElement('div', 'cost-limit-user-main');
    userMain.append(
      createElement('strong', '', user.name || '未命名用户'),
      createElement('span', 'cost-limit-cost', formatCurrency(user.costs?.totalCny || 0)),
    );
    userCell.append(
      userMain,
      createElement('div', 'cost-muted', user.email || ''),
      createElement('div', 'cost-muted', user.groupName || '未分组'),
    );
    const actionCell = createElement('td', 'cost-limit-action-cell');
    const edit = createElement('button', 'btn btn-secondary cost-limit-edit-btn', '设置');
    edit.type = 'button';
    edit.dataset.userId = String(userId);
    actionCell.append(edit);
    row.append(checkCell, userCell);
    resources.forEach((resource) => row.append(createUsageLimitCell(user.limits?.[resource.key], resource, { formatCount, statusLabel })));
    row.append(actionCell);
    tbody.append(row);
  });
  table.append(colgroup, thead, tbody);
  scroll.append(table);
  root.replaceChildren(scroll);
}

export function renderUsageLimitState(root, message) {
  if (root) root.replaceChildren(createEmptyState(message));
}
